import type { TenantConfig } from '@webmono/storefront-ui/types'

export const napoliPizza: TenantConfig = {
  id: 'napoli',
  slug: 'napoli-pizza',
  name: 'Napoli Pizza Co.',
  tagline: 'Authentic Italian Pizza Since 1985',
  description: 'Family-owned pizzeria serving traditional Neapolitan-style pizza with fresh, high-quality ingredients. A neighborhood favorite for over 35 years.',

  branding: {
    logo: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=200&h=200&fit=crop',
    logoAlt: 'Napoli Pizza Co. Logo',
    favicon: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=32&h=32&fit=crop',
    bannerImage: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=1200&h=400&fit=crop',
    ogImage: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=1200&h=630&fit=crop',
  },

  theme: {
    colors: {
      primary: '#2D6A4F',
      primaryForeground: '#FFFFFF',
      secondary: '#E8F5E9',
      secondaryForeground: '#2D6A4F',
      accent: '#C62828',
      accentForeground: '#FFFFFF',
      background: '#FAFAF8',
      foreground: '#1B1B1B',
      muted: '#F0F0ED',
      mutedForeground: '#5C5C5C',
      card: '#FFFFFF',
      cardForeground: '#1B1B1B',
      border: '#E0E0DC',
      input: '#E0E0DC',
      ring: '#2D6A4F',
      destructive: '#B71C1C',
      destructiveForeground: '#FFFFFF',
      success: '#1B5E20',
      successForeground: '#FFFFFF',
      warning: '#E65100',
      warningForeground: '#FFFFFF',
    },
    fonts: {
      heading: 'Playfair Display',
      body: 'Inter',
      mono: 'JetBrains Mono',
    },
    radius: 'md',
    spacing: 'normal',
  },

  contact: {
    address: '1234 Little Italy Lane',
    city: 'Brooklyn',
    state: 'NY',
    zip: '11201',
    country: 'USA',
    phone: '(718) 555-0456',
    email: 'ciao@napolipizza.com',
    coordinates: { lat: 40.6892, lng: -73.9857 },
  },

  operations: {
    timezone: 'America/New_York',
    hours: {
      monday: { open: '11:00', close: '22:00' },
      tuesday: { open: '11:00', close: '22:00' },
      wednesday: { open: '11:00', close: '22:00' },
      thursday: { open: '11:00', close: '22:00' },
      friday: { open: '11:00', close: '23:00' },
      saturday: { open: '11:00', close: '23:00' },
      sunday: { open: '12:00', close: '21:00' },
    },
    businessHours: { open: '11:00', close: '22:00' },
    taxRate: 0.08875,
    currency: 'USD',
    locale: 'en-US',
  },

  features: {
    pickup: true,
    delivery: true,
    tableOrdering: false,
    scheduledOrders: true,
    loyalty: true,
    promoCode: true,
    guestCheckout: true,
    accountRequired: false,
    tipping: true,
    specialInstructions: true,
    dietaryFilters: true,
  },

  fulfillment: {
    pickup: {
      enabled: true,
      leadTimeMinutes: 20,
      maxScheduleDays: 7,
    },
    delivery: {
      enabled: true,
      provider: 'in-house',
      flatFeeCents: 399,
      minimumOrderCents: 2000,
      radiusMiles: 3,
      leadTimeMinutes: 40,
      estimateMinutes: { min: 30, max: 45 },
    },
    tableService: {
      enabled: false,
      tableCount: 0,
      autoCloseMinutes: 0,
    },
  },

  loyalty: {
    enabled: true,
    programName: 'Napoli Rewards',
    pointsPerDollar: 10,
    tiers: [
      { id: 'bronze', name: 'Bronze', minPoints: 0, multiplier: 1, perks: ['Earn 10 points per $1'] },
      { id: 'silver', name: 'Silver', minPoints: 500, multiplier: 1.25, perks: ['Earn 12.5 points per $1', 'Free garlic knots on orders over $30'] },
      { id: 'gold', name: 'Gold', minPoints: 1500, multiplier: 1.5, perks: ['Earn 15 points per $1', 'Free appetizer monthly', 'Priority pickup'] },
    ],
    redemptionOptions: [
      { id: 'r1', name: '$5 Off', pointsCost: 500, discountCents: 500, description: 'Save $5 on your order' },
      { id: 'r2', name: '$10 Off', pointsCost: 900, discountCents: 1000, description: 'Save $10 on your order' },
      { id: 'r3', name: 'Free Medium Pizza', pointsCost: 1500, discountCents: 1899, description: 'One free medium 1-topping pizza' },
    ],
  },

  tipping: {
    enabled: true,
    presets: [15, 18, 20, 25],
    allowCustom: true,
  },

  i18n: {
    defaultLocale: 'en',
    supportedLocales: ['en'],
    translations: {
      en: {
        'cart.title': 'Your Order',
        'cart.empty': 'Your cart is empty',
        'checkout.pickup': 'Pickup',
        'checkout.delivery': 'Delivery',
      },
    },
  },

  legal: {
    privacyPolicyUrl: '/privacy',
    termsUrl: '/terms',
    allergenDisclaimer: 'Our pizzas contain wheat and dairy. We also handle nuts, eggs, and shellfish in our kitchen.',
  },

  social: {
    instagram: 'https://instagram.com/napolipizza',
    facebook: 'https://facebook.com/napolipizzaco',
    yelp: 'https://yelp.com/biz/napoli-pizza-co',
  },
}
